import { defineComponent, h, inject, mergeProps, } from 'vue';
import { WEFAT_COMPONENT_DOM_VERSION } from './basic-components.js';
import { WefatComponentRuntimeError } from './component-error.js';
import { useWefatHoverState } from './hover-state.js';
export const WEFAT_BUTTON_TYPES = Object.freeze(['default', 'primary', 'warn']);
export const WEFAT_BUTTON_SIZES = Object.freeze(['default', 'mini']);
export const WEFAT_BUTTON_FORM_TYPES = Object.freeze(['', 'submit', 'reset']);
export const WEFAT_BUTTON_OPEN_TYPES = Object.freeze(['', 'share']);
export const WEFAT_BUTTON_SHARE_DRIVER = Symbol('wefat-button-share-driver');
function includes(values, value) {
    return values.some((candidate) => candidate === value);
}
export function resolveWefatButtonContract(input) {
    const type = input.type ?? 'default';
    const size = input.size ?? 'default';
    const formType = input.formType ?? '';
    const openType = input.openType ?? '';
    if (!includes(WEFAT_BUTTON_OPEN_TYPES, openType)) {
        throw new WefatComponentRuntimeError('WF5003', `Button openType requires an unavailable capability adapter: ${openType || '<empty>'}`, 'Use open-type="share" with the Web Share adapter or replace the native-only open type with an explicit Web interaction.');
    }
    if (!includes(WEFAT_BUTTON_TYPES, type)) {
        throw new WefatComponentRuntimeError('WF5002', `Unsupported button type: ${type || '<empty>'}`, `Use one of the supported button types: ${WEFAT_BUTTON_TYPES.join(', ')}.`);
    }
    if (!includes(WEFAT_BUTTON_SIZES, size)) {
        throw new WefatComponentRuntimeError('WF5002', `Unsupported button size: ${size || '<empty>'}`, `Use one of the supported button sizes: ${WEFAT_BUTTON_SIZES.join(', ')}.`);
    }
    if (!includes(WEFAT_BUTTON_FORM_TYPES, formType)) {
        throw new WefatComponentRuntimeError('WF5002', `Unsupported button formType: ${formType || '<empty>'}`, 'Use an empty formType, submit, or reset.');
    }
    const classes = [
        'wefat-button',
        `wefat-button--${type}`,
        `wefat-button--${size}`,
        ...(input.plain === true ? ['wefat-button--plain'] : []),
    ];
    return Object.freeze({
        classes: Object.freeze(classes),
        formType,
        nativeType: formType === '' ? 'button' : formType,
        openType,
        size,
        type,
    });
}
function shareError(reason) {
    return new WefatComponentRuntimeError('WF5003', `Button share failed: ${reason}`, 'Use a browser with the Web Share API, invoke the button from a user gesture, and return supported title, desc, path, and imageUrl fields from onShareAppMessage.');
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function browserShareEnvironment() {
    return {
        currentTitle() {
            if (typeof document === 'undefined')
                throw shareError('document title is unavailable');
            return document.title;
        },
        currentUrl() {
            if (typeof window === 'undefined')
                throw shareError('current browser URL is unavailable');
            return window.location.href;
        },
        share(data) {
            if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
                throw shareError('Web Share API is unavailable');
            }
            return navigator.share(data);
        },
    };
}
function shareMessage(raw) {
    if (raw === undefined)
        return Object.freeze({});
    if (!isPlainRecord(raw))
        throw shareError('onShareAppMessage must return a plain object');
    const allowed = new Set(['desc', 'imageUrl', 'path', 'promise', 'title']);
    for (const key of Reflect.ownKeys(raw)) {
        if (typeof key !== 'string' || !allowed.has(key)) {
            throw shareError(`onShareAppMessage returned unsupported field ${String(key)}`);
        }
    }
    for (const key of ['desc', 'imageUrl', 'path', 'title']) {
        if (raw[key] !== undefined && typeof raw[key] !== 'string') {
            throw shareError(`onShareAppMessage field ${key} must be a string`);
        }
    }
    return raw;
}
async function resolvedShareMessage(raw) {
    const initial = shareMessage(raw);
    if (initial.promise === undefined)
        return initial;
    if (initial.promise === null
        || (typeof initial.promise !== 'object' && typeof initial.promise !== 'function')
        || typeof Reflect.get(initial.promise, 'then') !== 'function') {
        throw shareError('onShareAppMessage field promise must be a Promise');
    }
    const resolved = shareMessage(await initial.promise);
    return Object.freeze({ ...initial, ...resolved, promise: undefined });
}
function shareUrl(path, currentUrl) {
    if (path === undefined)
        return currentUrl;
    if (typeof path !== 'string' || !path.startsWith('/')) {
        throw shareError('onShareAppMessage path must be a root-relative Mini Program route');
    }
    let current;
    let target;
    try {
        current = new URL(currentUrl);
        if (current.hash.startsWith('#/')) {
            current.hash = path;
            return current.href;
        }
        target = new URL(path, current.origin);
    }
    catch {
        throw shareError('onShareAppMessage path or current browser URL is invalid');
    }
    if (target.origin !== current.origin) {
        throw shareError('onShareAppMessage path must stay on the generated application origin');
    }
    return target.href;
}
export function createBrowserButtonShareDriver(options) {
    if (!isPlainRecord(options) || typeof options.getActivePage !== 'function') {
        throw new TypeError('createBrowserButtonShareDriver requires an active-page getter');
    }
    const environment = options.environment ?? browserShareEnvironment();
    if (!isPlainRecord(environment)
        || typeof environment.currentTitle !== 'function'
        || typeof environment.currentUrl !== 'function'
        || typeof environment.share !== 'function') {
        throw new TypeError('createBrowserButtonShareDriver requires a complete browser share environment');
    }
    return Object.freeze({
        async share(event) {
            const page = options.getActivePage();
            if (page === null || typeof page.shareAppMessage !== 'function') {
                throw shareError('no active generated page is available');
            }
            try {
                const message = await resolvedShareMessage(page.shareAppMessage(event));
                const currentUrl = environment.currentUrl();
                const data = Object.freeze({
                    ...(message.desc === undefined ? {} : { text: message.desc }),
                    title: message.title === undefined ? environment.currentTitle() : message.title,
                    url: shareUrl(message.path, currentUrl),
                });
                await environment.share(data);
            }
            catch (error) {
                if (error instanceof WefatComponentRuntimeError)
                    throw error;
                throw shareError(error instanceof Error ? error.message : String(error));
            }
        },
    });
}
function datasetName(attribute) {
    if (!attribute.startsWith('data-'))
        return null;
    const source = attribute.slice(5);
    if (source.length === 0)
        return null;
    let result = '';
    let capitalize = false;
    for (const character of source) {
        if (character === '-') {
            if (capitalize)
                return null;
            capitalize = true;
            continue;
        }
        result += capitalize ? character.toUpperCase() : character;
        capitalize = false;
    }
    return capitalize ? null : result;
}
export function createWefatButtonShareEvent(attributes) {
    const dataset = Object.create(null);
    for (const [attribute, value] of Object.entries(attributes)) {
        const name = datasetName(attribute);
        if (name !== null)
            dataset[name] = value;
    }
    return Object.freeze({
        from: 'button',
        target: Object.freeze({ dataset: Object.freeze(dataset) }),
    });
}
function reportShareFailure(error) {
    queueMicrotask(() => {
        throw error instanceof WefatComponentRuntimeError
            ? error
            : shareError(error instanceof Error ? error.message : String(error));
    });
}
export const WefatButton = defineComponent({
    name: 'WefatButton',
    inheritAttrs: false,
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        formType: {
            type: String,
            default: '',
        },
        hidden: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        hoverClass: {
            type: String,
            default: 'none',
        },
        hoverStartTime: {
            type: [Number, String],
            default: 50,
        },
        hoverStayTime: {
            type: [Number, String],
            default: 400,
        },
        openType: {
            type: String,
            default: '',
        },
        plain: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'default',
        },
        type: {
            type: String,
            default: 'default',
        },
    },
    setup(props, { attrs, slots }) {
        const hover = useWefatHoverState(props);
        const shareDriver = inject(WEFAT_BUTTON_SHARE_DRIVER, null);
        return () => {
            const contract = resolveWefatButtonContract(props);
            return h('button', mergeProps(attrs, {
                'aria-busy': props.loading ? 'true' : undefined,
                class: [
                    ...contract.classes,
                    ...(props.loading ? ['wefat-button--loading'] : []),
                    ...(hover.active.value && props.hoverClass !== 'none' ? [props.hoverClass] : []),
                ],
                'data-wefat-button-size': contract.size,
                'data-wefat-button-type': contract.type,
                ...(contract.openType === '' ? {} : { 'data-wefat-button-open-type': contract.openType }),
                'data-wefat-component': 'button',
                'data-wefat-component-version': String(WEFAT_COMPONENT_DOM_VERSION),
                disabled: props.disabled ? true : undefined,
                hidden: props.hidden ? true : undefined,
                onClick: contract.openType === 'share'
                    ? () => {
                        if (shareDriver === null) {
                            reportShareFailure(shareError('the generated application did not install a Web Share driver'));
                            return;
                        }
                        void shareDriver.share(createWefatButtonShareEvent(attrs)).catch(reportShareFailure);
                    }
                    : undefined,
                onPointercancel: hover.onPointercancel,
                onPointerdown: hover.onPointerdown,
                onPointerleave: hover.onPointerleave,
                onPointerup: hover.onPointerup,
                type: contract.nativeType,
            }), [
                props.loading
                    ? h('span', {
                        'aria-hidden': 'true',
                        class: 'wefat-button__loading',
                    })
                    : null,
                slots.default?.(),
            ]);
        };
    },
});
//# sourceMappingURL=button-component.js.map